import pdfMake from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import useDate from '@/services/utils/day';
import figureFormatter from '@/services/utils/figureFormatter';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const sulaimanLipiFont = {
    SulaimanLipi: {
        normal: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
        italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
        bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf'
    }
}
pdfMake.fonts = sulaimanLipiFont;

const productionTargetActualPdf = () => {
    const { formatDate } = useDate();
    const { onlyCommaFormat } = figureFormatter();

    const exportToPDF = (company, rows, filters, userName) => {
        pdfMake.fonts = sulaimanLipiFont;

        const docDefinition = {
            pageSize: 'A4',
            pageOrientation: 'portrait',
            pageMargins: [20, 100, 20, 80],
            header: getHeader(company),
            footer: getFooter(userName),
            content: getContent(rows, filters),
            defaultStyle: {
                fontSize: 8,
                color: '#333',
                font: 'SulaimanLipi'
            },
            info: {
                title: 'Production Target vs Actual'
            }
        };

        pdfMake.createPdf(docDefinition).open();
    };

    const getContent = (rows, filters) => {
        return [
            { text: 'Production Target vs Actual', fontSize: 14, bold: true, alignment: 'center', margin: [0, 0, 0, 6] },
            getFilterSummary(filters),
            getRowsTable(rows, filters)
        ];
    };

    const getFilterSummary = (filters) => {
        return {
            text: `Product Type: ${filters.productTypeLabel}    Grouped By: ${groupByLabel(filters.groupBy)}    Period: ${filters.start} to ${filters.end}`,
            fontSize: 9,
            alignment: 'center',
            margin: [0, 0, 0, 12]
        };
    };

    const groupByLabel = (groupBy) => {
        if (groupBy === 'item') return 'Item';
        return 'Category';
    };

    const getRowsTable = (rows, filters) => {
        return {
            table: {
                headerRows: 1,
                widths: ['40%', '15%', '15%', '15%', '15%'],
                body: [getTableHeaderRow(filters), ...rows.map(getTableDataRow), getTotalRow(rows)]
            },
            layout: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                fillColor: (rowIndex) => (rowIndex === 0) ? '#f3f2f7' : null
            }
        };
    };

    const getTableHeaderRow = (filters) => {
        return [
            { text: `${groupByLabel(filters.groupBy)} Name`, bold: true },
            { text: 'Target', bold: true, alignment: 'right' },
            { text: 'Actual', bold: true, alignment: 'right' },
            { text: 'Variance', bold: true, alignment: 'right' },
            { text: 'Percentage', bold: true, alignment: 'right' }
        ];
    };

    const getTableDataRow = (row) => {
        return [
            { text: row.name || '' },
            { text: onlyCommaFormat(row.target_qty), alignment: 'right' },
            { text: onlyCommaFormat(row.actual_qty), alignment: 'right' },
            { text: onlyCommaFormat(row.variance_qty), alignment: 'right' },
            { text: `${Number(row.percentage || 0)}%`, alignment: 'right' }
        ];
    };

    const sumOf = (rows, column) => {
        return rows.reduce((total, row) => total + Number(row[column] || 0), 0);
    };

    const totalPercentage = (targetTotal, actualTotal) => {
        if (! targetTotal) return 0;
        return Math.round((actualTotal / targetTotal) * 100 * 100) / 100;
    };

    const getTotalRow = (rows) => {
        const targetTotal = sumOf(rows, 'target_qty');
        const actualTotal = sumOf(rows, 'actual_qty');

        return [
            { text: 'Total', bold: true },
            { text: onlyCommaFormat(targetTotal), bold: true, alignment: 'right' },
            { text: onlyCommaFormat(actualTotal), bold: true, alignment: 'right' },
            { text: onlyCommaFormat(actualTotal - targetTotal), bold: true, alignment: 'right' },
            { text: `${totalPercentage(targetTotal, actualTotal)}%`, bold: true, alignment: 'right' }
        ];
    };

    const getHeader = (company) => {
        return {
            margin: [30, 25, 30, 10],
            columns: [
                {
                    alignment: 'left',
                    image: company.logo64,
                    maxHeight: 60
                },
                {
                    alignment: 'right',
                    stack: [
                        company.name,
                        'Address: ' + company.address,
                        'Phone: ' + company.phone,
                        'E-mail: ' + company.email
                    ]
                }
            ]
        };
    };

    const getFooter = (userName) => {
        return {
            margin: [25, 0],
            columns: [
                {
                    width: '80%',
                    alignment: 'left',
                    text: 'N.B: This is a system generated documents and requires no manual signature.',
                    margin: [5, 53, 0, 0],
                    fontSize: 8
                },
                {
                    width: '20%',
                    alignment: 'right',
                    fontSize: 8,
                    margin: [0, 33, 0, 0],
                    stack: [
                        { text: 'Printed by', bold: true },
                        { text: userName },
                        { text: formatDate(new Date()) }
                    ]
                }
            ]
        };
    };

    return {
        exportToPDF
    };
};

export default productionTargetActualPdf;
